import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { 
  DollarSign, TrendingUp, TrendingDown, Plus, Edit2, Trash2,
  Calendar, Filter, Download, PieChart, BarChart3, Loader,
  ArrowUpRight, ArrowDownRight, Wallet, Receipt, AlertCircle, Lock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const FinancialControl = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [accessError, setAccessError] = useState('');
  const [categories, setCategories] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  
  const [formData, setFormData] = useState({
    type: 'receita',
    category: '',
    amount: '',
    description: '',
    payment_method: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    notes: '',
    recurrence: 'none',
    recurrence_count: '',
    recurrence_end_date: '',
  });

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/financial/categories`, {
        withCredentials: true
      });
      setCategories(response.data);
      setHasAccess(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setHasAccess(false);
        setAccessError(error.response?.data?.detail || 'Acesso negado');
      }
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('type', filterType);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      
      const response = await axios.get(
        `${API_URL}/financial/transactions?${params.toString()}`,
        { withCredentials: true }
      );
      setTransactions(response.data.transactions);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Erro ao carregar transações');
      }
    }
  }, [filterType, filterStartDate, filterEndDate]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      
      const response = await axios.get(
        `${API_URL}/financial/summary?${params.toString()}`,
        { withCredentials: true }
      );
      setSummary(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Erro ao carregar resumo:', error);
      }
    }
  }, [filterStartDate, filterEndDate]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCategories();
      await fetchTransactions();
      await fetchSummary();
      setLoading(false);
    };
    loadData();
  }, [fetchCategories, fetchTransactions, fetchSummary]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        type: formData.type,
        category: formData.category,
        amount: parseFloat(formData.amount),
        description: formData.description,
        payment_method: formData.payment_method || null,
        date: formData.date,
        time: formData.time || null,
        notes: formData.notes || null,
      };

      if (editingTransaction) {
        // Se estava numa série, perguntar escopo
        let scope = 'one';
        if (editingTransaction.series_id) {
          const choice = window.prompt(
            'Este lançamento pertence a uma série recorrente. Aplicar mudanças em:\n' +
              '  1 = Apenas esta ocorrência\n' +
              '  2 = Esta e as próximas\n' +
              '  3 = Todas da série\n\n' +
              'Digite 1, 2 ou 3:',
            '1'
          );
          if (choice === '2') scope = 'future';
          else if (choice === '3') scope = 'all';
          else scope = 'one';
        }
        await axios.put(
          `${API_URL}/financial/transactions/${editingTransaction.transaction_id}`,
          { ...payload, edit_scope: scope },
          { withCredentials: true }
        );
        toast.success('Transação atualizada!');
      } else {
        payload.recurrence = formData.recurrence || 'none';
        if (payload.recurrence !== 'none') {
          if (formData.recurrence_count) payload.recurrence_count = parseInt(formData.recurrence_count, 10);
          if (formData.recurrence_end_date) payload.recurrence_end_date = formData.recurrence_end_date;
        }
        const res = await axios.post(
          `${API_URL}/financial/transactions`,
          payload,
          { withCredentials: true }
        );
        toast.success(res.data.message || 'Transação registrada!');
      }

      resetForm();
      fetchTransactions();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar transação');
    }
  };

  const resetForm = () => {
    setIsDialogOpen(false);
    setEditingTransaction(null);
    setFormData({
      type: 'receita',
      category: '',
      amount: '',
      description: '',
      payment_method: '',
      date: new Date().toISOString().split('T')[0],
      time: '',
      notes: '',
      recurrence: 'none',
      recurrence_count: '',
      recurrence_end_date: '',
    });
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      type: transaction.type,
      category: transaction.category,
      amount: transaction.amount,
      description: transaction.description,
      payment_method: transaction.payment_method || '',
      date: transaction.date,
      time: transaction.time || '',
      notes: transaction.notes || '',
      recurrence: transaction.recurrence || 'none',
      recurrence_count: '',
      recurrence_end_date: '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (transaction) => {
    const id = typeof transaction === 'string' ? transaction : transaction.transaction_id;
    const txn = typeof transaction === 'string' ? null : transaction;
    let scope = 'one';
    if (txn?.series_id) {
      const choice = window.prompt(
        'Este lançamento pertence a uma série recorrente. Excluir:\n' +
          '  1 = Apenas esta ocorrência\n' +
          '  2 = Esta e as próximas\n' +
          '  3 = Todas da série\n\n' +
          'Digite 1, 2 ou 3:',
        '1'
      );
      if (!choice) return;
      if (choice === '2') scope = 'future';
      else if (choice === '3') scope = 'all';
    } else if (!window.confirm('Tem certeza que deseja excluir esta transação?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/financial/transactions/${id}?scope=${scope}`, {
        withCredentials: true,
      });
      toast.success('Transação excluída!');
      fetchTransactions();
      fetchSummary();
    } catch (error) {
      toast.error('Erro ao excluir transação');
    }
  };

  const getCategoryLabel = (type, category) => {
    if (type === 'receita') {
      return categories.revenue_labels?.[category] || category;
    }
    return categories.expense_labels?.[category] || category;
  };

  const getPaymentLabel = (method) => {
    return categories.payment_method_labels?.[method] || method;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  // Se não tem acesso (plano não Pro+)
  if (!hasAccess && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Controle Financeiro
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              {accessError || 'Este recurso está disponível a partir do plano Pro.'}
            </p>
            <Button
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              onClick={() => window.location.href = '/seller/dashboard'}
            >
              Fazer Upgrade
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader className="w-12 h-12 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="financial-title">
              Controle Financeiro
            </h1>
            <p className="text-lg text-gray-600">
              Gerencie suas receitas e despesas
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            data-testid="add-transaction-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Transação
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Receitas</p>
                  <p className="text-2xl font-bold text-green-600 mt-1" data-testid="total-revenue">
                    {formatCurrency(summary.total_revenue)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Despesas</p>
                  <p className="text-2xl font-bold text-red-600 mt-1" data-testid="total-expenses">
                    {formatCurrency(summary.total_expenses)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Lucro Líquido</p>
                  <p className={`text-2xl font-bold mt-1 ${summary.net_profit >= 0 ? 'text-indigo-600' : 'text-red-600'}`} data-testid="net-profit">
                    {formatCurrency(summary.net_profit)}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${summary.net_profit >= 0 ? 'bg-indigo-100' : 'bg-red-100'}`}>
                  <Wallet className={`w-6 h-6 ${summary.net_profit >= 0 ? 'text-indigo-600' : 'text-red-600'}`} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Margem de Lucro</p>
                  <p className={`text-2xl font-bold mt-1 ${summary.profit_margin >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="profit-margin">
                    {summary.profit_margin.toFixed(1)}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <PieChart className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtros:</span>
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              data-testid="filter-type"
            >
              <option value="">Todos os tipos</option>
              <option value="receita">Receitas</option>
              <option value="despesa">Despesas</option>
            </select>

            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                className="h-9 w-36"
                data-testid="filter-start-date"
              />
              <span className="text-gray-500">até</span>
              <Input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                className="h-9 w-36"
                data-testid="filter-end-date"
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilterType('');
                setFilterStartDate('');
                setFilterEndDate('');
              }}
            >
              Limpar
            </Button>
          </div>
        </div>

        {/* Transactions List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-indigo-600" />
              Transações ({transactions.length})
            </h2>
          </div>

          {transactions.length === 0 ? (
            <div className="p-12 text-center">
              <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nenhuma transação encontrada
              </h3>
              <p className="text-gray-600 mb-6">
                Comece registrando sua primeira receita ou despesa
              </p>
              <Button onClick={() => setIsDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Transação
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {transactions.map((txn) => (
                <div
                  key={txn.transaction_id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`transaction-${txn.transaction_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        txn.type === 'receita' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {txn.type === 'receita' ? (
                          <ArrowUpRight className="w-5 h-5 text-green-600" />
                        ) : (
                          <ArrowDownRight className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{txn.description}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {getCategoryLabel(txn.type, txn.category)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(txn.date)}
                          </span>
                          {txn.payment_method && (
                            <span className="text-xs text-gray-500">
                              • {getPaymentLabel(txn.payment_method)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className={`text-lg font-bold ${
                        txn.type === 'receita' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {txn.type === 'receita' ? '+' : '-'} {formatCurrency(txn.amount)}
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(txn)}
                          data-testid={`edit-transaction-${txn.transaction_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(txn)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-transaction-${txn.transaction_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category Breakdown */}
        {summary && (summary.total_revenue > 0 || summary.total_expenses > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {/* Revenue by Category */}
            {Object.keys(summary.revenue_by_category).length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Receitas por Categoria
                </h3>
                <div className="space-y-3">
                  {Object.entries(summary.revenue_by_category).map(([cat, value]) => (
                    <div key={cat} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{cat}</span>
                      <span className="font-medium text-green-600">{formatCurrency(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Expenses by Category */}
            {Object.keys(summary.expenses_by_category).length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  Despesas por Categoria
                </h3>
                <div className="space-y-3">
                  {Object.entries(summary.expenses_by_category).map(([cat, value]) => (
                    <div key={cat} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{cat}</span>
                      <span className="font-medium text-red-600">{formatCurrency(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Transaction Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="transaction-dialog-title">
              {editingTransaction ? 'Editar Transação' : 'Nova Transação'}
            </DialogTitle>
            <DialogDescription>
              Registre uma receita ou despesa
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type Selection */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, type: 'receita', category: '' }))}
                className={`p-3 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
                  formData.type === 'receita'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="type-receita"
              >
                <TrendingUp className="w-5 h-5" />
                Receita
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, type: 'despesa', category: '' }))}
                className={`p-3 rounded-lg border-2 transition-colors flex items-center justify-center gap-2 ${
                  formData.type === 'despesa'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid="type-despesa"
              >
                <TrendingDown className="w-5 h-5" />
                Despesa
              </button>
            </div>

            <div>
              <Label>Valor (R$) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0,00"
                required
                data-testid="transaction-amount"
              />
            </div>

            <div>
              <Label>Descrição *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ex: Venda de curso online"
                required
                data-testid="transaction-description"
              />
            </div>

            <div>
              <Label>Categoria *</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                required
                data-testid="transaction-category"
              >
                <option value="">Selecione...</option>
                {formData.type === 'receita' 
                  ? categories.revenue_categories?.map(cat => (
                      <option key={cat} value={cat}>
                        {categories.revenue_labels?.[cat]}
                      </option>
                    ))
                  : categories.expense_categories?.map(cat => (
                      <option key={cat} value={cat}>
                        {categories.expense_labels?.[cat]}
                      </option>
                    ))
                }
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="transaction-date"
                />
              </div>
              <div>
                <Label>Forma de Pagamento</Label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="transaction-payment"
                >
                  <option value="">Selecione...</option>
                  {categories.payment_methods?.map(method => (
                    <option key={method} value={method}>
                      {categories.payment_method_labels?.[method]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Observações (opcional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas adicionais..."
                rows={2}
                data-testid="transaction-notes"
              />
            </div>

            {/* ─── Recorrência ─── (apenas em criação; editar séries usa scope) */}
            {!editingTransaction && (
              <div className="border-t border-slate-200 pt-4 space-y-3">
                <div>
                  <Label>Recorrência</Label>
                  <select
                    value={formData.recurrence}
                    onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    data-testid="transaction-recurrence"
                  >
                    {(categories.recurrence_types || ['none']).map((r) => (
                      <option key={r} value={r}>
                        {categories.recurrence_labels?.[r] || r}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.recurrence !== 'none' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Horário (opcional)</Label>
                      <input
                        type="time"
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="transaction-time"
                      />
                    </div>
                    <div>
                      <Label>Nº de repetições</Label>
                      <input
                        type="number"
                        min="1"
                        placeholder="Ilimitado (deixe vazio)"
                        value={formData.recurrence_count}
                        onChange={(e) => setFormData({ ...formData, recurrence_count: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="transaction-recurrence-count"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Data final da recorrência (opcional)</Label>
                      <input
                        type="date"
                        value={formData.recurrence_end_date}
                        onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="transaction-recurrence-end"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1" data-testid="transaction-submit">
                {editingTransaction ? 'Atualizar' : 'Registrar'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
